// =============================================================================
// OELOX — IndexedDB layer (Dexie) for POS PWA offline storage
// =============================================================================
// PRIMARY SOURCE OF TRUTH for all offline data.
// React Query syncs network → IndexedDB → UI (never the reverse).
// =============================================================================

import Dexie, { type Table } from "dexie";

// ── TTL constants ───────────────────────────────────────────────────────────

const MENU_CACHE_TTL = 24 * 60 * 60 * 1000;   // 24 hours
const ORDERS_CACHE_TTL = 24 * 60 * 60 * 1000;  // 24 hours (was 30 min — too short for offline shifts)

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface QueuedAction {
  id: string;
  type: string;
  payload: unknown;
  timestamp: number;
  retryCount: number;
}

export interface CartLine {
  /** Compound identity — required for multi-tenant isolation (Phase 1). */
  tenant_id: string;
  user_id: string;
  menu_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  image_url?: string | null;
}

/** @deprecated Legacy unscoped cart row (pre-v8). Cleared on upgrade — never remapped. */
interface LegacyCartLine {
  menu_item_id: string;
  item_name?: string;
  quantity?: number;
  unit_price?: number;
  image_url?: string | null;
  tenant_id?: string;
  user_id?: string;
}

export interface OperationalSessionRow {
  id: "current";
  userId: string;
  email: string | null;
  tenantId: string;
  tenantName: string;
  currency: string;
  status: "ACTIVE" | "AUTH_DEGRADED" | "SESSION_INVALID";
  establishedAt: number;
  lastValidatedAt: number;
}

export interface CachedMenuItem {
  id: string;
  tenant_id: string;
  name: string;
  base_price: number;
  category_id: string | null;
  image_url: string | null;
  cached_at: number;
}

export interface CachedTenant {
  id: string;
  name: string;
  currency: string;
  profile_tenant_id: string;
  user_id: string;
  cached_at: number;
}

export interface CachedCategory {
  id: string;
  tenant_id: string;
  name: string;
  sort_order: number;
  cached_at: number;
}

export interface CachedOrder {
  id: string;
  local_id: string;
  server_id: string | null;
  tenant_id: string;
  displayNumber: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string | null;
  status: string;
  deskStep: string;
  total: number;
  currency: string;
  source: string;
  fulfillment_type: string;
  payment_method: string;
  payment_status: string;
  item_count: number;
  items: Array<{
    menu_item_id: string;
    item_name: string;
    quantity: number;
    unit_price: number;
  }>;
  sync_status: "pending" | "synced";
  created_at: string;
  updated_at: string;
  cached_at: number;
}

export interface CachedDriver {
  id: string;
  tenant_id: string;
  name: string;
  phone: string | null;
  vehicle_type: string | null;
  is_online: boolean;
  is_verified: boolean;
  collects_cod: boolean;
  link_status: string;
  cached_at: number;
}

// ── Database ────────────────────────────────────────────────────────────────

export class OeloxPosDB extends Dexie {
  queue!: Table<QueuedAction, string>;
  dead_letter!: Table<QueuedAction, string>;
  cart!: Table<CartLine, string>;
  menu_items!: Table<CachedMenuItem, string>;
  menu_categories!: Table<CachedCategory, string>;
  cached_orders!: Table<CachedOrder, string>;
  cached_drivers!: Table<CachedDriver, string>;
  tenant_cache!: Table<CachedTenant, string>;
  operational_session!: Table<OperationalSessionRow, string>;

  constructor() {
    super("oelox-pos-db");
    this.version(6).stores({
      queue: "id, type, timestamp, retryCount",
      dead_letter: "id, type, timestamp, retryCount",
      cart: "menu_item_id",
      menu_items: "id, tenant_id, category_id",
      menu_categories: "id, tenant_id, sort_order",
      cached_orders: "id, tenant_id, deskStep, created_at",
      cached_drivers: "id, tenant_id, link_status",
      tenant_cache: "user_id",
    });
    this.version(7).stores({
      queue: "id, type, timestamp, retryCount",
      dead_letter: "id, type, timestamp, retryCount",
      cart: "menu_item_id",
      menu_items: "id, tenant_id, category_id",
      menu_categories: "id, tenant_id, sort_order",
      cached_orders: "id, local_id, server_id, tenant_id, deskStep, sync_status, created_at",
      cached_drivers: "id, tenant_id, link_status",
      tenant_cache: "user_id",
    });
    // Phase 1: operational_session + tenant/user-scoped cart.
    // Queue, dead_letter, menu, orders, drivers, tenant_cache are preserved.
    // Unscoped legacy cart rows are discarded (never remapped across tenants).
    this.version(8).stores({
      queue: "id, type, timestamp, retryCount",
      dead_letter: "id, type, timestamp, retryCount",
      cart: "[tenant_id+menu_item_id], tenant_id, user_id, menu_item_id",
      menu_items: "id, tenant_id, category_id",
      menu_categories: "id, tenant_id, sort_order",
      cached_orders: "id, local_id, server_id, tenant_id, deskStep, sync_status, created_at",
      cached_drivers: "id, tenant_id, link_status",
      tenant_cache: "user_id",
      operational_session: "id, userId, tenantId, status",
    }).upgrade(async (tx) => {
      const legacyCart = tx.table("cart");
      const rows = await legacyCart.toArray() as LegacyCartLine[];
      await legacyCart.clear();
      const scoped = rows.filter(
        (r) => typeof r.tenant_id === "string" && r.tenant_id.length > 0
          && typeof r.user_id === "string" && r.user_id.length > 0
          && typeof r.menu_item_id === "string"
      ) as CartLine[];
      if (scoped.length > 0) {
        await legacyCart.bulkPut(scoped);
      }
      // Unscoped rows intentionally dropped — do not assign a guessed tenant.
    });
  }
}

export const db = new OeloxPosDB();

// ── In-memory cache for synchronous component access ───────────────────────
// IndexedDB is async, but React components need data synchronously on mount.
// This module-level cache mirrors IndexedDB so components can render instantly
// from memory without waiting for async IndexedDB reads.

const menuItemsMemCache = new Map<string, CachedMenuItem[]>();
const categoriesMemCache = new Map<string, CachedCategory[]>();
let cartMemCache: CartLine[] = [];
const ordersMemCache = new Map<string, CachedOrder[]>();  // key: tenantId:deskStep

/** Synchronous read — returns [] if not yet cached. */
export function getCachedMenuItemsSync(tenantId: string): CachedMenuItem[] {
  return menuItemsMemCache.get(tenantId) ?? [];
}

/** Synchronous read — returns [] if not yet cached. */
export function getCachedCategoriesSync(tenantId: string): CachedCategory[] {
  return categoriesMemCache.get(tenantId) ?? [];
}

/**
 * Synchronous cart read for the currently loaded scope.
 * Call loadCartForScope(tenantId, userId) during hydration / tenant switch first.
 */
export function getCartSync(tenantId?: string, userId?: string): CartLine[] {
  if (!tenantId) return cartMemCache;
  return cartMemCache.filter(
    (l) => l.tenant_id === tenantId && (!userId || l.user_id === userId)
  );
}

/** Synchronous read — returns [] if not yet cached. Key: `${tenantId}:${deskStep}`. */
export function getCachedOrdersSync(tenantId: string, deskStep?: string): CachedOrder[] {
  const key = deskStep ? `${tenantId}:${deskStep}` : `${tenantId}:__all__`;
  return ordersMemCache.get(key) ?? [];
}

/** One-time preload from IndexedDB → memory on module init (page load). */
let primeDone = false;
export async function primeMenuCacheFromIndexedDB(): Promise<void> {
  if (primeDone) return;
  try {
    const allItems = await db.menu_items.toArray();
    // Rebuild from scratch — never append (prevents duplicates on re-call)
    const itemsByTenant = new Map<string, CachedMenuItem[]>();
    for (const item of allItems) {
      const arr = itemsByTenant.get(item.tenant_id);
      if (arr) arr.push(item);
      else itemsByTenant.set(item.tenant_id, [item]);
    }
    menuItemsMemCache.clear();
    for (const [tid, items] of itemsByTenant) {
      menuItemsMemCache.set(tid, items);
    }

    const allCats = await db.menu_categories.orderBy("sort_order").toArray();
    const catsByTenant = new Map<string, CachedCategory[]>();
    for (const cat of allCats) {
      const arr = catsByTenant.get(cat.tenant_id);
      if (arr) arr.push(cat);
      else catsByTenant.set(cat.tenant_id, [cat]);
    }
    categoriesMemCache.clear();
    for (const [tid, cats] of catsByTenant) {
      categoriesMemCache.set(tid, cats);
    }

    primeDone = true;
  } catch { /* IndexedDB unavailable — silent */ }
}

// Cart is preloaded in main.tsx via loadCart() alongside primeMenuCacheFromIndexedDB().
// Both must complete before React renders to prevent UI flash.
//
// Orders and drivers are also preloaded below so all IndexedDB-backed pages work offline
// on first paint — no "empty screen" flash.

/** One-time preload: orders from IndexedDB → ordersMemCache. */
let ordersPrimeDone = false;
export async function primeOrdersFromIndexedDB(): Promise<void> {
  if (ordersPrimeDone) return;
  try {
    const allOrders = await db.cached_orders.toArray();
    const byTenant = new Map<string, CachedOrder[]>();
    for (const o of allOrders) {
      const arr = byTenant.get(o.tenant_id);
      if (arr) arr.push(o);
      else byTenant.set(o.tenant_id, [o]);
    }
    ordersMemCache.clear();
    for (const [tid, orders] of byTenant) {
      // Per-deskStep keys
      for (const step of ["pending", "kitchen", "ready", "driver", "done"]) {
        ordersMemCache.set(`${tid}:${step}`, orders.filter((o) => o.deskStep === step));
      }
      ordersMemCache.set(`${tid}:__all__`, orders);
    }
    ordersPrimeDone = true;
  } catch { /* silent */ }
}

/** One-time preload: drivers from IndexedDB → driversMemCache. */
let driversPrimeDone = false;
export async function primeDriversFromIndexedDB(): Promise<void> {
  if (driversPrimeDone) return;
  try {
    const allDrivers = await db.cached_drivers.toArray();
    const byTenant = new Map<string, CachedDriver[]>();
    for (const d of allDrivers) {
      const arr = byTenant.get(d.tenant_id);
      if (arr) arr.push(d);
      else byTenant.set(d.tenant_id, [d]);
    }
    driversMemCache.clear();
    for (const [tid, drivers] of byTenant) {
      driversMemCache.set(tid, drivers);
    }
    driversPrimeDone = true;
  } catch { /* silent */ }
}

// Preload is now called explicitly from main.tsx (awaited before first render).
// The fire-and-forget duplicate has been removed to prevent double-priming
// which caused menu items to appear twice in the catalog.

// ── Cart helpers (tenant + user scoped) ─────────────────────────────────────

let cartScopeTenantId = "";
let cartScopeUserId = "";

/** @deprecated Use loadCartForScope — kept as no-op-safe alias that clears mem if no scope. */
export async function loadCart(): Promise<CartLine[]> {
  return loadCartForScope(cartScopeTenantId, cartScopeUserId);
}

export async function loadCartForScope(tenantId: string, userId: string): Promise<CartLine[]> {
  cartScopeTenantId = tenantId;
  cartScopeUserId = userId;
  if (!tenantId || !userId) {
    cartMemCache = [];
    return [];
  }
  try {
    const lines = await db.cart
      .where("tenant_id")
      .equals(tenantId)
      .and((l) => l.user_id === userId)
      .toArray();
    cartMemCache = lines;
    return lines;
  } catch {
    cartMemCache = [];
    return [];
  }
}

/**
 * Persist cart for a specific tenant/user. Replaces only that scope's rows.
 * Lines without tenant_id/user_id are stamped from the arguments.
 */
export async function saveCart(
  lines: CartLine[],
  tenantId?: string,
  userId?: string
): Promise<void> {
  const tid = tenantId || cartScopeTenantId;
  const uid = userId || cartScopeUserId;
  if (!tid || !uid) {
    cartMemCache = [];
    return;
  }
  const stamped = lines.map((l) => ({
    ...l,
    tenant_id: tid,
    user_id: uid,
  }));
  cartMemCache = stamped;
  cartScopeTenantId = tid;
  cartScopeUserId = uid;
  try {
    const existing = await db.cart
      .where("tenant_id")
      .equals(tid)
      .and((l) => l.user_id === uid)
      .primaryKeys();
    if (existing.length > 0) await db.cart.bulkDelete(existing);
    if (stamped.length > 0) await db.cart.bulkPut(stamped);
  } catch { /* silent */ }
}

export async function clearCart(tenantId?: string, userId?: string): Promise<void> {
  const tid = tenantId || cartScopeTenantId;
  const uid = userId || cartScopeUserId;
  await clearCartForScope(tid, uid);
}

export async function clearCartForScope(tenantId: string, userId: string): Promise<void> {
  if (
    cartScopeTenantId === tenantId && cartScopeUserId === userId
  ) {
    cartMemCache = [];
  } else if (!tenantId || !userId) {
    cartMemCache = [];
  }
  if (!tenantId || !userId) return;
  try {
    const keys = await db.cart
      .where("tenant_id")
      .equals(tenantId)
      .and((l) => l.user_id === userId)
      .primaryKeys();
    if (keys.length > 0) await db.cart.bulkDelete(keys);
  } catch { /* silent */ }
}

// ── Menu cache helpers ──────────────────────────────────────────────────────

export async function getCachedMenuItems(tenantId: string): Promise<CachedMenuItem[]> {
  try {
    const items = await db.menu_items.where("tenant_id").equals(tenantId).toArray();
    const cutoff = Date.now() - MENU_CACHE_TTL;
    return items.filter((i) => i.cached_at > cutoff);
  } catch {
    return [];
  }
}

export async function cacheMenuItems(tenantId: string, items: CachedMenuItem[]): Promise<void> {
  // Write to in-memory cache synchronously first (instant component access)
  menuItemsMemCache.set(tenantId, items);
  try {
    await db.menu_items.where("tenant_id").equals(tenantId).delete();
    const now = Date.now();
    await db.menu_items.bulkPut(items.map((i) => ({ ...i, cached_at: now })));
  } catch { /* silent */ }
}

// ── Tenant cache helpers ────────────────────────────────────────────────────

export async function cacheTenant(data: CachedTenant): Promise<void> {
  try {
    await db.tenant_cache.put({ ...data, cached_at: Date.now() });
  } catch { /* silent */ }
}

export async function getCachedTenant(userId: string): Promise<CachedTenant | null> {
  try {
    const t = await db.tenant_cache.get(userId);
    if (!t) return null;
    // 7-day TTL for tenant cache — but NEVER delete when offline.
    // Expired cache is better than no cache (offline-first principle).
    if (Date.now() - t.cached_at > 7 * 24 * 60 * 60 * 1000) {
      if (typeof navigator !== "undefined" && navigator.onLine) {
        await db.tenant_cache.delete(userId);
        return null; // online: force refresh from network
      }
      // offline: return expired cache — it's all we have
    }
    return t;
  } catch {
    return null;
  }
}

// ── Category cache helpers ──────────────────────────────────────────────────

export async function getCachedCategories(tenantId: string): Promise<CachedCategory[]> {
  try {
    return await db.menu_categories.where("tenant_id").equals(tenantId).sortBy("sort_order");
  } catch {
    return [];
  }
}

export async function cacheCategories(tenantId: string, cats: CachedCategory[]): Promise<void> {
  // Write to in-memory cache synchronously first (instant component access)
  categoriesMemCache.set(tenantId, cats);
  try {
    await db.menu_categories.where("tenant_id").equals(tenantId).delete();
    if (cats.length > 0) await db.menu_categories.bulkPut(cats);
  } catch { /* silent */ }
}

// ── Orders cache helpers ────────────────────────────────────────────────────

export async function getCachedOrders(tenantId: string, deskStep?: string): Promise<CachedOrder[]> {
  try {
    let collection = db.cached_orders.where("tenant_id").equals(tenantId);
    if (deskStep) collection = collection.and((o) => o.deskStep === deskStep);
    const orders = await collection.sortBy("created_at");
    const cutoff = Date.now() - ORDERS_CACHE_TTL;
    return orders.filter((o) => o.cached_at > cutoff);
  } catch {
    return [];
  }
}

export async function cacheOrders(tenantId: string, orders: CachedOrder[]): Promise<void> {
  try {
    // Only keep last 100 orders per tenant
    const existing = await db.cached_orders.where("tenant_id").equals(tenantId).toArray();
    const keep = new Map(existing.map((o) => [o.id, o]));
    for (const o of orders) keep.set(o.id, o);
    const all = Array.from(keep.values()).sort((a, b) => b.created_at.localeCompare(a.created_at));
    const trimmed = all.slice(0, 500);
    await db.cached_orders.where("tenant_id").equals(tenantId).delete();
    if (trimmed.length > 0) await db.cached_orders.bulkPut(trimmed);

    // Update sync memory cache per deskStep
    for (const step of ["pending", "kitchen", "ready", "driver", "done"]) {
      const key = `${tenantId}:${step}`;
      ordersMemCache.set(key, trimmed.filter((o) => o.deskStep === step));
    }
    ordersMemCache.set(`${tenantId}:__all__`, trimmed);
  } catch { /* silent */ }
}

export async function upsertCachedOrder(order: CachedOrder): Promise<void> {
  try {
    const existing = await db.cached_orders.get(order.id);
    await db.cached_orders.put({
      ...existing,
      ...order,
      cached_at: Date.now(),
    });

    const all = await db.cached_orders.where("tenant_id").equals(order.tenant_id).toArray();
    for (const step of ["pending", "kitchen", "ready", "driver", "done"]) {
      ordersMemCache.set(`${order.tenant_id}:${step}`, all.filter((o) => o.deskStep === step));
    }
    ordersMemCache.set(`${order.tenant_id}:__all__`, all);
  } catch { /* silent */ }
}

export async function markCachedOrderSynced(
  tenantId: string,
  localId: string,
  serverOrder: {
    id: string;
    displayNumber: string;
    status: string;
    deskStep: string;
    source: string;
    fulfillment_type: string;
    payment_method: string;
    payment_status: string;
    total: number;
    currency: string;
    customer_name: string;
    customer_phone: string | null;
    customer_address: string | null;
    item_count: number;
    created_at: string;
    updated_at: string;
  }
): Promise<void> {
  try {
    const local = await db.cached_orders.where("local_id").equals(localId).first();
    if (!local) return;
    await upsertCachedOrder({
      ...local,
      id: local.local_id,
      local_id: local.local_id,
      server_id: serverOrder.id,
      displayNumber: serverOrder.displayNumber,
      status: serverOrder.status,
      deskStep: serverOrder.deskStep,
      source: serverOrder.source,
      fulfillment_type: serverOrder.fulfillment_type,
      payment_method: serverOrder.payment_method,
      payment_status: serverOrder.payment_status,
      total: serverOrder.total,
      currency: serverOrder.currency,
      customer_name: serverOrder.customer_name,
      customer_phone: serverOrder.customer_phone,
      customer_address: serverOrder.customer_address,
      item_count: serverOrder.item_count,
      created_at: local.created_at,
      updated_at: serverOrder.updated_at,
      sync_status: "synced",
      cached_at: Date.now(),
      tenant_id: tenantId,
    });
  } catch { /* silent */ }
}

// ── Driver cache helpers ────────────────────────────────────────────────────

const driversMemCache = new Map<string, CachedDriver[]>();

/** Synchronous read — returns [] if not yet cached. */
export function getCachedDriversSync(tenantId: string): CachedDriver[] {
  return driversMemCache.get(tenantId) ?? [];
}

export async function getCachedDrivers(tenantId: string): Promise<CachedDriver[]> {
  try {
    return await db.cached_drivers.where("tenant_id").equals(tenantId).toArray();
  } catch {
    return [];
  }
}

export async function cacheDrivers(tenantId: string, drivers: CachedDriver[]): Promise<void> {
  // Write to in-memory cache synchronously first (instant component access)
  driversMemCache.set(tenantId, drivers);
  try {
    await db.cached_drivers.where("tenant_id").equals(tenantId).delete();
    if (drivers.length > 0) await db.cached_drivers.bulkPut(drivers);
  } catch { /* silent */ }
}

// ── One-time migration from localStorage ────────────────────────────────────

const MIGRATED_KEY = "oelox_idxb_migrated";

export async function migrateFromLocalStorage(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const currentVersion = localStorage.getItem(MIGRATED_KEY);

    // v1 migration: queue + dead-letter
    if (!currentVersion) {
      const QUEUE_KEY = "oelox_offline_queue";
      const DEAD_KEY = "oelox_dead_letter_queue";

      const rawQueue = localStorage.getItem(QUEUE_KEY);
      if (rawQueue) {
        try {
          const items: QueuedAction[] = JSON.parse(rawQueue);
          if (items.length > 0) await db.queue.bulkPut(items);
        } catch { /* corrupt */ }
        localStorage.removeItem(QUEUE_KEY);
      }

      const rawDead = localStorage.getItem(DEAD_KEY);
      if (rawDead) {
        try {
          const items: QueuedAction[] = JSON.parse(rawDead);
          if (items.length > 0) await db.dead_letter.bulkPut(items);
        } catch { /* corrupt */ }
        localStorage.removeItem(DEAD_KEY);
      }
    }

    // v2 migration: cart
    if (!currentVersion || currentVersion === "v1") {
      const rawCart = localStorage.getItem("oelox-pos-cart");
      if (rawCart) {
        try {
          const items: CartLine[] = JSON.parse(rawCart);
          if (items.length > 0) {
            await db.cart.clear();
            await db.cart.bulkPut(items);
          }
        } catch { /* corrupt */ }
        localStorage.removeItem("oelox-pos-cart");
      }
    }

    localStorage.setItem(MIGRATED_KEY, "v2");
  } catch { /* IndexedDB unavailable */ }
}
